import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, IconButton,
  Dialog, Backdrop, Checkbox, FormControlLabel, Divider,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const ConflictDialog = ({ open, onClose, conflicts, newPct, onConfirm }) => {
  const [selected, setSelected] = useState(() => conflicts.map(c => c.id));
  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);
  const toggleAll = () => setSelected(p => p.length === conflicts.length ? [] : conflicts.map(c => c.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      BackdropComponent={Backdrop} BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } }}
      PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', overflow: 'hidden' } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningAmberRoundedIcon sx={{ color: 'var(--warning)', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Artículos con descuento</Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.68rem', color: 'var(--text-muted)' }}>Ya tienen un descuento diferente al {newPct}%</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
      </Box>

      <Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
        <FormControlLabel
          control={<Checkbox checked={selected.length === conflicts.length} indeterminate={selected.length > 0 && selected.length < conflicts.length} onChange={toggleAll} size="small" sx={{ '&.Mui-checked': { color: 'var(--accent)' } }} />}
          label={<Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>Seleccionar todos</Typography>}
        />
        <Divider sx={{ my: 1, borderColor: 'var(--border)' }} />
        {conflicts.map((item) => (
          <Box key={item.id} onClick={() => toggle(item.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 0.5, borderRadius: 'var(--radius-md)', cursor: 'pointer', border: selected.includes(item.id) ? '1.5px solid var(--accent)' : '1.5px solid var(--border)', backgroundColor: selected.includes(item.id) ? 'var(--accent-soft)' : 'var(--bg-card)', transition: 'all 0.15s ease', '&:hover': { borderColor: 'var(--accent)' } }}>
            <Checkbox checked={selected.includes(item.id)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: 'var(--accent)' } }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>Cód: {item.codigo || item.id}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--warning)', fontFamily: 'var(--font-mono)' }}>{item.descuentoLinea}% actual</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>→ {newPct}% nuevo</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <Button onClick={onClose} size="small" sx={{ borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', color: 'var(--text-secondary)', border: '1.5px solid var(--border)', px: 2, '&:hover': { bgcolor: 'var(--bg-subtle)' } }}>Cancelar</Button>
        <Button onClick={() => { onConfirm(selected); onClose(); }} disabled={selected.length === 0} size="small" variant="contained"
          sx={{ borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', px: 3, backgroundColor: 'var(--accent)', boxShadow: 'none', '&:hover': { backgroundColor: 'var(--accent-hover)' }, '&:disabled': { backgroundColor: 'var(--border)' } }}>
          Reemplazar ({selected.length})
        </Button>
      </Box>
    </Dialog>
  );
};

const SeccionDescuento = ({ cartList, descuentoMaximo = 100, onApplyGeneral }) => {
  const [generalPct, setGeneralPct] = useState('');
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingPct, setPendingPct] = useState(0);
  const pctValue = parseFloat(generalPct);

  const isApplyDisabled =
    !generalPct ||
    isNaN(pctValue) ||
    pctValue <= 0 ||
    pctValue > descuentoMaximo;
  const handleGeneralChange = (e) => {
    const val = e.target.value.replace(',', '.');

    if (/^\d{0,3}(\.\d{0,1})?$/.test(val)) {
      const num = parseFloat(val);

      if (
        val === '' ||
        (num >= 0 && num <= descuentoMaximo)
      ) {
        setGeneralPct(val);
      }
    }
  };

  const handleApplyAll = () => {
    const pct = parseFloat(generalPct) || 0;
    if (pct <= 0 || pct > descuentoMaximo) return;

    const conConflicto = cartList.filter(item => item.descuentoLinea > 0 && item.descuentoLinea !== pct);

    if (conConflicto.length > 0) {
      setConflicts(conConflicto);
      setPendingPct(pct);
      setConflictOpen(true);
    } else {
      onApplyGeneral(pct, cartList.map(i => i.id));
    }
  };

  const handleConfirmReplace = (idsToReplace) => {
    const sinConflicto = cartList.filter(i => !i.descuentoLinea || i.descuentoLinea === pendingPct).map(i => i.id);
    onApplyGeneral(pendingPct, [...sinConflicto, ...idsToReplace]);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--bg-card)',
        mb: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid var(--border)'
        }}
      >
        {/* Icono */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--success-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <LocalOfferOutlinedIcon
            sx={{ color: 'var(--success)', fontSize: 18 }}
          />
        </Box>

        {/* Título + máximo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            flex: 1
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '0.88rem',
              color: 'var(--text-primary)'
            }}
          >
            Descuento del Vendedor
          </Typography>

          <Typography
            sx={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.65rem',
              color: 'var(--text-muted)'
            }}
          >
            Máximo permitido: {descuentoMaximo}%
          </Typography>
        </Box>

        {/* Input + botón */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <TextField
            value={generalPct}
            onChange={handleGeneralChange}
            placeholder="0"
            size="small"
            inputProps={{
              style: {
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '0.9rem',
                fontFamily: 'var(--font-mono)'
              },
              inputMode: 'decimal'
            }}
            sx={{
              width: 70,
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--radius-sm)',
                height: 36,
                '& fieldset': {
                  borderColor: 'var(--border)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent)'
                }
              }
            }}
          />

          <PercentOutlinedIcon
            sx={{
              fontSize: 16,
              color: 'var(--text-muted)'
            }}
          />

          <Button
            onClick={handleApplyAll}
            disabled={isApplyDisabled}
            size="small"
            variant="contained"
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-body)',
              fontWeight: 700,
              fontSize: '0.78rem',
              textTransform: 'none',
              px: 2,
              backgroundColor: 'var(--success)',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#059669'
              },
              '&:disabled': {
                backgroundColor: 'var(--border)',
                color: 'var(--text-muted)'
              }
            }}
          >
            Aplicar a todos
          </Button>
        </Box>
      </Box>

      <ConflictDialog
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        conflicts={conflicts}
        newPct={pendingPct}
        onConfirm={handleConfirmReplace}
      />
    </Paper>
  );
};

export default SeccionDescuento;